import * as React from 'react'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ButtonStyle from '@enums/ButtonStyle'
import { Routes } from '@enums/Routes'
import {
  useGetEmployeeEducationDestinationQuery,
  useLazyGetDestinationReportQuery,
} from '@services/employeeEducationDestination'
import { formatServerDate } from '@utils/date'
import { EmployeeEducationStat } from '@models/employeeEducationStats'
import { User } from '@models/user'
import { ProgressStatuses } from '@enums/ProgressStatuses'
import { TestResult } from '@models/statistic'
import Table from '@organisms/Table'
import Loader from '@organisms/Loader'
import { Tab } from '@organisms/Tabs'
import DownloadButton from '@organisms/DownloadButton'
import AccordionBodyField from '@organisms/AccordionBodyField'
import Title from '@organisms/Title'
import StyledButton from '@organs/StyledButton'
import SecondaryButton from '@organisms/SecondaryButton'
import Modal from '@molecules/Modal'
import ConfirmModalContent from '@molecules/ConfirmModalContent'
import PrimaryButton from '@organisms/PrimaryButton'
import useMe from '@hooks/useMe'
import useDownloadCertificate from '@hooks/useDownloadCertificate'
import passBear from '@interface-images/ellipse-41.png'
import nopassBear from '@interface-images/ellipse-40.png'
import { getTableData } from './utils'
import {
  CourseCompletionActions,
  CourseCompletionBear,
  CourseCompletionBody,
  CourseCompletionDescription,
  CourseCompletionModalContent,
  CourseCompletionTitle,
  FinalActions,
  FinalResultBear,
  FinalResultBody,
  FinalResultContent,
  FinalResultCourseTitle,
  FinalResultHeader,
  FinalResultRows,
  FinalPageStatus,
  FinalResultPanel,
  FinalResultPage,
  FinalResultRow,
  FinalResultTitle,
  FinalTopRow,
  Footer,
  ProgressWrapper,
  StyledSubtitle,
  StyledTitle,
} from './styled'

const getDestinationId = (educationDestinationIri?: string) => {
  if (!educationDestinationIri) {
    return undefined
  }

  const value = Number(
    educationDestinationIri
      .split('/')
      .filter(Boolean)
      .slice(-1)[0]
  )

  return Number.isFinite(value) ? value : undefined
}

const getDestinationNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  const numericValue = Number(value)

  return Number.isInteger(numericValue) && numericValue >= 0
    ? numericValue
    : undefined
}

const formatDuration = (secondsDuration?: number) => {
  const safeSeconds = Math.max(0, Number(secondsDuration) || 0)
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const seconds = safeSeconds % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${String(seconds).padStart(2, '0')}`
}

export type FinalTestResultState = {
  educationDestinationIri: string
  courseTitle: string
  courseId: string | number
  educationId: string | number
  themeId: string | number
  passPercentage?: number | null
  questionsCount?: number | null
  questionsPassed?: number | null
  secondsDuration?: number | null
  testSuccess?: boolean
  passingScore?: number | null
  showCourseCompletionModal?: boolean
}

const COLUMNS = [
  {
    title: 'Дата',
    dataIndex: 'date',
    size: 250,
  },
  {
    title: 'Длительность',
    dataIndex: 'duration',
  },
  {
    title: 'Результат',
    dataIndex: 'result',
  },
]

export type RowItem = Record<'date' | 'duration' | 'result', ReactNode> & {
  key: string
}

type Props = {
  user: User
  stats: EmployeeEducationStat[]
  finalTestResult?: FinalTestResultState
}

type FinalTestResultProps = {
  finalEducationDestinationId: number
  finalTestResult: FinalTestResultState
  courseCompletionDestination?: EmployeeEducationStat['employeeEducationDestinations'][number]
}

const FinalTestResultView: FC<FinalTestResultProps> = ({
  finalEducationDestinationId,
  finalTestResult,
  courseCompletionDestination,
}) => {
  const [isCourseCompletionModalVisible, setIsCourseCompletionModalVisible] =
    useState(Boolean(finalTestResult.showCourseCompletionModal))
  const navigate = useNavigate()
  const {
    data: finalTestDestinationData,
    isLoading: finalTestDestinationIsLoading,
    refetch: refetchFinalTestDestination,
  } = useGetEmployeeEducationDestinationQuery(finalEducationDestinationId as number, {
    skip: finalEducationDestinationId <= 0,
    refetchOnMountOrArgChange: true,
  })
  const completionDestinationData =
    finalTestDestinationData || courseCompletionDestination

  // Счётчики пройденных тем берём в расчёт только после того, как завершится наш
  // СОБСТВЕННЫЙ перезапрос назначения, запущенный уже на экране результатов. К этому
  // моменту итоговый тест точно записан на бэкенде (postEnd завершается до навигации,
  // а пересчёт статуса и счётчиков там синхронный), поэтому именно наш перезапрос
  // читает финальные данные, где итоговый учтён.
  //
  // На перезапросы, которые могли остаться «в полёте» ещё с экрана прохождения
  // (например инвалидация кэша после ответа на очередной вопрос теста, когда итоговый
  // ещё не зачтён), полагаться нельзя: они возвращают счётчики без итогового и, успев
  // завершиться первыми, заставляли модалку мигнуть неверной плашкой «Осталось пройти
  // незавершённые темы». Поэтому ждём результат ровно своего перезапроса, а не любого
  // перехода isFetching.
  const isDestinationQueryActive = finalEducationDestinationId > 0
  const [areThemeCountsFresh, setAreThemeCountsFresh] = useState(false)

  useEffect(() => {
    if (!isDestinationQueryActive) {
      return undefined
    }

    let isActive = true
    const destinationRefetch = refetchFinalTestDestination()

    const markThemeCountsFresh = () => {
      // Даже если перезапрос упал, снимаем блокировку показа, чтобы модалка не зависла
      // скрытой навсегда: покажем по тем данным, что есть.
      if (isActive) {
        setAreThemeCountsFresh(true)
      }
    }

    destinationRefetch.unwrap().then(markThemeCountsFresh, markThemeCountsFresh)

    return () => {
      isActive = false
      destinationRefetch.abort()
    }
    // refetchFinalTestDestination стабилен для одного id, перезапускаемся только при
    // смене назначения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDestinationQueryActive, finalEducationDestinationId])

  const areThemeCountsReady = !isDestinationQueryActive || areThemeCountsFresh

  const finalTestResultData = useMemo(() => {
    const lastTestResult = finalTestDestinationData?.testResults?.length
      ? [...finalTestDestinationData.testResults].sort(
          (left, right) =>
            new Date(right.date).getTime() - new Date(left.date).getTime()
        )[0]
      : null

    if (
      finalTestResult.questionsCount !== undefined &&
      finalTestResult.questionsPassed !== undefined
    ) {
      return {
        date: lastTestResult?.date || '',
        questionsCount: finalTestResult.questionsCount ?? 0,
        questionsPassed: finalTestResult.questionsPassed ?? 0,
        secondsDuration:
          lastTestResult?.secondsDuration ||
          finalTestResult.secondsDuration ||
          0,
      }
    }

    return lastTestResult
  }, [
    finalTestDestinationData?.testResults,
    finalTestResult.questionsCount,
    finalTestResult.questionsPassed,
    finalTestResult.secondsDuration,
  ])

  const passingPercent = Number(
    finalTestResult.passingScore ??
      finalTestResult.passPercentage ??
      100
  )
  const passedPercent = finalTestResultData
    ? Math.round(
        (finalTestResultData.questionsPassed /
          Math.max(finalTestResultData.questionsCount, 1)) *
          100
      )
    : 0
  const isPassed = (() => {
    if (finalTestResult.testSuccess === undefined) {
      return (
        Boolean(finalTestResultData) &&
        passedPercent > 0 &&
        passedPercent >= passingPercent
      )
    }

    return finalTestResult.testSuccess === true
  })()
  const duration = formatDuration(finalTestResultData?.secondsDuration || 0)
  const hasAllThemesPassed =
    Number(completionDestinationData?.passedThemesCount) >=
    Number(completionDestinationData?.totalThemesCount)
  const isFullyPassedNow = isPassed && hasAllThemesPassed
  // Завершённость курса монотонна: пройденную тему нельзя «отменить». Зафиксируем факт
  // полного прохождения, чтобы поздно долетевший устаревший ответ назначения (счётчики
  // без учёта итогового) уже не переключил плашку обратно на «Осталось пройти».
  const [wasFullyPassed, setWasFullyPassed] = useState(false)
  useEffect(() => {
    if (isFullyPassedNow) {
      setWasFullyPassed(true)
    }
  }, [isFullyPassedNow])
  const isCoursePassedCompletely = isFullyPassedNow || wasFullyPassed
  const courseCompletionModalTitle = isCoursePassedCompletely
    ? 'Вы успешно прошли курс'
    : 'Отлично! Осталось пройти незавершенные темы'
  const courseCompletionModalDescription = isCoursePassedCompletely
    ? ''
    : 'Для полного завершения курса пройдите оставшиеся темы (с жёлтым и серым индикаторами).'
  const courseCompletionButtonTitle = isCoursePassedCompletely
    ? 'Хорошо'
    : 'Перейти'

  const closeCourseCompletionModal = () => {
    setIsCourseCompletionModalVisible(false)
    navigate(Routes.STATISTIC, {
      replace: true,
      state: {
        finalTestResult: {
          ...finalTestResult,
          showCourseCompletionModal: false,
        },
      },
    })
  }

  const handleOpenStudy = () => {
    navigate(Routes.STUDY)
  }

  const handleGoCourse = () => {
    navigate(
      `${Routes.COURSES}/${finalTestResult.courseId}/education/${finalTestResult.educationId}`
    )
  }

  const handleRetry = () => {
    navigate(
      `/themes/${finalTestResult.themeId}/course/${finalTestResult.courseId}/education/${finalTestResult.educationId}`
    )
  }

  const handleCourseCompletionAction = () => {
    if (isCoursePassedCompletely) {
      closeCourseCompletionModal()
      return
    }

    closeCourseCompletionModal()
    handleGoCourse()
  }

  return (
    <Loader isLoading={finalTestDestinationIsLoading}>
      {/* Модалку показываем только когда счётчики тем свежие (areThemeCountsReady),
          иначе на первом кадре после итогового мелькает неверная плашка
          «Осталось пройти незавершённые темы» по устаревшим данным. */}
      {isCourseCompletionModalVisible && isPassed && areThemeCountsReady && (
        <Modal
          visible
          onModalClose={closeCourseCompletionModal}
          width={isCoursePassedCompletely ? 'fit-content' : '1288px'}
          withoutClose
        >
          {isCoursePassedCompletely ? (
            <ConfirmModalContent
              iconSrc={passBear}
              title={courseCompletionModalTitle}
              rightContent={
                <PrimaryButton
                  onClick={handleCourseCompletionAction}
                  big
                  autoWidth
                >
                  {courseCompletionButtonTitle}
                </PrimaryButton>
              }
            />
          ) : (
            <CourseCompletionModalContent>
              <CourseCompletionBear
                src={nopassBear}
                alt=""
                aria-hidden="true"
              />
              <CourseCompletionBody $isCoursePassedCompletely={false}>
                <CourseCompletionTitle>
                  {courseCompletionModalTitle}
                </CourseCompletionTitle>
                {courseCompletionModalDescription && (
                  <CourseCompletionDescription>
                    {courseCompletionModalDescription}
                  </CourseCompletionDescription>
                )}
                <CourseCompletionActions $isCoursePassedCompletely={false}>
                  <StyledButton
                    buttonStyle={ButtonStyle.PRIMARY}
                    onClick={handleCourseCompletionAction}
                    autoWidth
                  >
                    {courseCompletionButtonTitle}
                  </StyledButton>
                  <SecondaryButton
                    onClick={closeCourseCompletionModal}
                    autoWidth
                  >
                    Закрыть
                  </SecondaryButton>
                </CourseCompletionActions>
              </CourseCompletionBody>
            </CourseCompletionModalContent>
          )}
        </Modal>
      )}
      <FinalResultPage>
        <FinalTopRow>
          <StyledButton
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={handleOpenStudy}
            autoWidth
          >
            ← Мое обучение
          </StyledButton>
        </FinalTopRow>
        <FinalResultCourseTitle>
          {finalTestResult.courseTitle}
        </FinalResultCourseTitle>
        <FinalResultPanel isSuccess={isPassed}>
          <FinalResultContent>
            <FinalResultBear
              src={isPassed ? passBear : nopassBear}
              alt={
                isPassed
                  ? 'Успешное прохождение теста'
                  : 'Неуспешное прохождение теста'
              }
            />
            <FinalResultBody>
              <FinalResultHeader>
                <FinalResultTitle>Итоговое тестирование</FinalResultTitle>
                <FinalPageStatus isSuccess={isPassed}>
                  {isPassed
                    ? 'Вы прошли итоговое тестирование'
                    : 'Вы не прошли итоговое тестирование'}
                </FinalPageStatus>
              </FinalResultHeader>
              <FinalResultRows>
                <FinalResultRow>
                  <span>Затрачено времени:</span> {duration}
                </FinalResultRow>
                <FinalResultRow>
                  <span>Вы набрали:</span> {passedPercent}%
                </FinalResultRow>
                <FinalResultRow>
                  <span>Проходной балл:</span> {passingPercent}%
                </FinalResultRow>
              </FinalResultRows>
              <FinalActions>
                <StyledButton
                  buttonStyle={ButtonStyle.PRIMARY}
                  onClick={handleRetry}
                  autoWidth
                >
                  Повторить прохождение
                </StyledButton>
                <StyledButton
                  buttonStyle={ButtonStyle.SECONDARY}
                  onClick={handleGoCourse}
                  autoWidth
                >
                  Весь курс
                </StyledButton>
              </FinalActions>
            </FinalResultBody>
          </FinalResultContent>
        </FinalResultPanel>
      </FinalResultPage>
    </Loader>
  )
}

const StatisticTemplate: FC<Props> = ({ user, stats = [], finalTestResult }) => {
  const { isEmployee } = useMe()
  const { getCert, isLoading: certificateIsLoading } = useDownloadCertificate()
  const [selected, setSelected] = useState(stats[0])
  const [selectedBottom, setSelectedBottom] = useState(
    selected?.employeeEducationDestinations[0]
  )
  const finalEducationDestinationId = finalTestResult
    ? getDestinationId(finalTestResult.educationDestinationIri) ??
      getDestinationNumber(finalTestResult.educationId)
    : undefined
  const courseCompletionDestination = finalTestResult
    ? stats
        .find((stat) => String(stat.id) === String(finalTestResult.courseId))
        ?.employeeEducationDestinations.find((destination) =>
          finalEducationDestinationId && finalEducationDestinationId > 0
            ? destination.id === finalEducationDestinationId
            : true
        )
    : undefined

  useEffect(() => {
    setSelectedBottom(selected?.employeeEducationDestinations[0])
  }, [selected])

  const [getCourseReport, { isLoading: getCourseReportIsLoading }] =
    useLazyGetDestinationReportQuery()

  const { data, isLoading } = useGetEmployeeEducationDestinationQuery(
    selectedBottom?.id,
    {
      skip: !selectedBottom,
    }
  )
  const testResults = data?.testResults || ([] as TestResult[])
  const tableData = getTableData(testResults)

  if (finalTestResult && finalEducationDestinationId !== undefined) {
    return (
      <FinalTestResultView
        finalEducationDestinationId={finalEducationDestinationId}
        finalTestResult={finalTestResult}
        courseCompletionDestination={courseCompletionDestination}
      />
    )
  }

  const handleDownloadReport = () => {
    void getCourseReport(selectedBottom.id)
  }

  const handleDownloadCertificate = () => {
    void getCert({
      educationId: selectedBottom.id,
      courseId: selected.id,
      userId: user.id,
    })
  }

  const ownerCompanyTitle = selected?.ownerCompany?.title?.trim().toLowerCase()
  const canDownloadCertificate =
    selected?.isClientCourse === false ||
    ownerCompanyTitle === 'школа экспертов' ||
    (selected?.isClientCourse === undefined && !selected?.ownerCompany)

  if (stats.length === 0) {
    return (
      <>
        <StyledTitle>{user.fullName}</StyledTitle>
        <Title mySize="h2">Обучения не найдены</Title>
      </>
    )
  }

  return (
    <>
      <StyledTitle>{user.fullName}</StyledTitle>
      <ProgressWrapper>
        {stats.map((item) => (
          <Tab
            id="id"
            tabSize="small"
            onClick={() => setSelected(item)}
            isActive={selected?.id === item.id}
            title={item.title}
            children={null}
            key={item.id}
          />
        ))}
      </ProgressWrapper>
      <ProgressWrapper withBottomMargin>
        {selected?.employeeEducationDestinations.map((item) => (
          <Tab
            id="id"
            tabSize="small"
            onClick={() => setSelectedBottom(item)}
            isActive={selectedBottom?.id === item.id}
            title={formatServerDate(item.educationDestination.createdAt)}
            badgeColor={
              item.currentStatus === ProgressStatuses.PASSED
                ? 'green'
                : item.currentStatus === ProgressStatuses.IN_PROCESS
                ? 'orange'
                : 'gray'
            }
            children={null}
            key={item.id}
          />
        ))}
      </ProgressWrapper>
      {selectedBottom && (
        <Loader isLoading={isLoading}>
          <StyledSubtitle>Ведомость</StyledSubtitle>
          <AccordionBodyField
            label="Дата регистрации"
            value={
              user.registeredAt
                ? formatServerDate(user.registeredAt)
                : undefined
            }
          />
          <AccordionBodyField
            label="Курс"
            value={selected.title}
            withoutFixedHeight
          />
          <AccordionBodyField
            label="процент изученного материала"
            value={
              selectedBottom?.progress >= 0
                ? selectedBottom?.progress + ' %'
                : undefined
            }
          />
          <AccordionBodyField
            label="количество изученных тем"
            value={
              selectedBottom?.passedThemesCount >= 0 &&
              selectedBottom?.totalThemesCount >= 0
                ? (selectedBottom?.passedThemesCount || 0) +
                  ' из ' +
                  selectedBottom?.totalThemesCount
                : undefined
            }
          />
          {tableData.length > 0 && (
            <>
              <StyledSubtitle>Итоговое тестирование</StyledSubtitle>
              <Table data={tableData} columns={COLUMNS} />
            </>
          )}
          {selectedBottom?.progress === 100 && (
            <Footer>
              {canDownloadCertificate && (
                <DownloadButton
                  onClick={handleDownloadCertificate}
                  isLoading={certificateIsLoading}
                >
                  Сохранить сертификат
                </DownloadButton>
              )}
              {!isEmployee && (
                <DownloadButton
                  onClick={handleDownloadReport}
                  isLoading={getCourseReportIsLoading}
                >
                  Сохранить отчёт
                </DownloadButton>
              )}
            </Footer>
          )}
        </Loader>
      )}
    </>
  )
}

export default StatisticTemplate
